"use client"

import { DatesProvider } from "@mantine/dates"
import {
  QueryClient,
  QueryClientProvider,
  isServer,
} from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import dayjs from "dayjs"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"
import { usePathname } from "next/navigation"
import React, { useEffect } from "react"

import { Cookies } from "@/config/enums"
import { TLoginUserData } from "@/schemas/user.schema"
import { useAppStore } from "@/app/(store)/appStore"
import { initializeTwilioSyncClient } from "@/lib/sync.helper"
import { AppProgressBar as ProgressBar } from "next-nprogress-bar"

import { consola } from "consola"
import { ModalsProvider } from "@mantine/modals"
import { isDJUIEnabled } from "@/lib/featureFlags.utils"

dayjs.extend(utc)
dayjs.extend(timezone)

consola.wrapAll()
consola.level = process.env.NODE_ENV === "production" ? 1 : 5

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
      },
    },
  })
}

let browserQueryClient: QueryClient | undefined = undefined

function getQueryClient() {
  if (isServer) {
    return makeQueryClient()
  } else {
    if (!browserQueryClient) browserQueryClient = makeQueryClient()
    return browserQueryClient
  }
}

function getCookie(cName: string): string {
  const name = cName + "="
  const cDecoded = decodeURIComponent(document.cookie) //to be careful
  const cArr = cDecoded.split("; ")

  return cArr.reduce((accumulator, currentValue) => {
    if (accumulator.length > 0) return accumulator

    if (currentValue.indexOf(name) === 0)
      return currentValue.substring(name.length)

    return accumulator
  }, "")
}

interface IClientProviders extends React.PropsWithChildren {
  userData: TLoginUserData & { xAuth: string }
}

export function ClientProviders({ children, userData }: IClientProviders) {
  return (
    <SubClientProviders userData={userData}>
      {children}
      <ProgressBar
        height="4px"
        color="#5EB5F3"
        options={{ showSpinner: false }}
        shallowRouting
      />
    </SubClientProviders>
  )
}

function SubClientProviders({ children, userData }: IClientProviders) {
  const pathname = usePathname()
  const { setUserData, changeSidebarMenuState } = useAppStore()
  const queryClient = getQueryClient()

  useEffect(() => {
    changeSidebarMenuState(false)
    const intervalChecker = setInterval(() => {
      const launcherIDCookie = getCookie(Cookies.Enum["launcher-id"])

      if (
        pathname &&
        pathname.startsWith("/login") &&
        launcherIDCookie &&
        launcherIDCookie.length > 0
      ) {
        clearInterval(intervalChecker)

        // Go to home
        const homeFullURL = !process.env.NEXT_PUBLIC_FRONT_URL
          ? "/"
          : `http${process.env.NEXT_PUBLIC_FRONT_URL.includes("localhost") ? "" : "s"}://${process.env.NEXT_PUBLIC_FRONT_URL}/`
        window.location.replace(homeFullURL)
      }

      if (
        (!pathname ||
          (!pathname.startsWith("/login") &&
            !pathname.startsWith("/recover-password"))) &&
        !launcherIDCookie
      ) {
        clearInterval(intervalChecker)

        // Go to login
        const loginFullURL = !process.env.NEXT_PUBLIC_FRONT_URL
          ? "/login"
          : `http${process.env.NEXT_PUBLIC_FRONT_URL.includes("localhost") ? "" : "s"}://${process.env.NEXT_PUBLIC_FRONT_URL}/login`
        window.location.replace(loginFullURL)
      }
    }, 5 * 1_000)

    return () => {
      if (intervalChecker) clearInterval(intervalChecker)
    }
  }, [changeSidebarMenuState, pathname])

  useEffect(() => {
    setUserData(userData)

    if (userData.account.status !== "LOGGED_OUT") {
      if (isDJUIEnabled()) {
        initializeTwilioSyncClient({
          initialUserData: userData,
          token: userData.twilio.syncToken,
        })

        // initializeTwilioPhoneClient({
        //   token: userData.twilio.twimlToken,
        // })
      }
    }
  }, [setUserData, userData])
  return (
    <DatesProvider
      settings={{
        timezone: "UTC",
      }}
    >
      <QueryClientProvider client={queryClient}>
        <ModalsProvider>{children}</ModalsProvider>
        {process.env.NEXT_PUBLIC_SHOW_DEV_TOOLS == "true" && (
          <ReactQueryDevtools />
        )}
      </QueryClientProvider>
    </DatesProvider>
  )
}
